import { useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";

import { Notification } from "@/api";
import { HighlightText, UserAvatar } from "@/components";
import { useAppProperties } from "@/hooks";
import { useNotifications, useTranslate } from "@/providers/context";
import { cn, getUrlFromFilename } from "@/utils";

export const NotificationCard = ({
  notification,
  isUnread,
  isLast,
}: {
  notification: Notification;
  isUnread: boolean;
  isLast: boolean;
}) => {
  const { heading, content, data, send_after, community_id, url } =
    notification;
  const { data: properties, communityEnabled } = useAppProperties();
  const { formatDate } = useTranslate();
  const navigate = useNavigate();
  const { markAsRead } = useNotifications();

  const communityParams = useMemo(() => {
    if (!url) {
      return undefined;
    }

    try {
      const urlObj = new URL(url);
      const typeParam = urlObj.searchParams.get("$type");
      const idParam = urlObj.searchParams.get("$id");

      if (!typeParam || !idParam) {
        return undefined;
      }

      const isCommunityType =
        typeParam === "communityPost" || typeParam === "communityComment";

      if (!isCommunityType) {
        return undefined;
      }

      return {
        contentType: typeParam,
        contentId: idParam,
      };
    } catch {
      return undefined;
    }
  }, [url]);

  const isClickable = !!communityEnabled && !!communityParams && !!community_id;

  const onClick = useCallback(() => {
    if (isClickable) {
      markAsRead();

      const { contentType, contentId } = communityParams;

      const contentParam =
        contentType === "communityComment" ? "comment_id" : "post_id";

      navigate(
        `/community?community_id=${community_id}&${contentParam}=${contentId}`,
      );
    }
  }, [isClickable, community_id, communityParams, markAsRead, navigate]);

  const isUserNotification = data?.is_user_notification;

  const avatarUrl = useMemo(() => {
    if (isUserNotification) {
      return data?.avatar_url;
    }
    if (properties?.AppIconImage) {
      return getUrlFromFilename(properties.AppIconImage);
    }
    return undefined;
  }, [isUserNotification, data?.avatar_url, properties?.AppIconImage]);

  return (
    <div className="flex w-full flex-col">
      <div
        className={cn(
          "flex w-full gap-3 rounded-xl p-3",
          isClickable && "cursor-pointer hover:bg-surface-secondary",
        )}
        onClick={isClickable ? onClick : undefined}
      >
        {isUserNotification ? (
          <UserAvatar
            name={data?.display_name}
            imageUrl={avatarUrl}
            className="h-7 w-7"
          />
        ) : (
          <div className="flex h-7 w-7 shrink-0 bg-surface-disabled">
            {avatarUrl && (
              <img
                className="h-full w-full rounded-[4px] object-cover"
                src={avatarUrl}
                alt="App Icon"
                onError={(e) => {
                  e.currentTarget.style.display = "none";
                }}
              />
            )}
          </div>
        )}
        <div className="flex w-full flex-col gap-1">
          <div className="flex min-h-6 w-full items-center justify-between gap-2">
            {data?.display_name ? (
              <div className="p2-medium">
                <HighlightText
                  text={heading}
                  highlight={`@${data?.display_name}`}
                />
              </div>
            ) : (
              <div className="p2-bold">{heading}</div>
            )}
            {isUnread && (
              <div className="h-2 w-2 shrink-0 rounded-full bg-highlightPrimary" />
            )}
          </div>
          <div className="p2-regular line-clamp-4 whitespace-pre-wrap">
            {content}
          </div>
          <div className="caption-bold mt-2 text-item-secondary">
            {formatDate(new Date(send_after * 1000))}
          </div>
        </div>
      </div>
      {!isLast && <div className="my-[22px] h-[1px] w-full bg-border" />}
    </div>
  );
};
